import { faBan, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import React from "react";
import { Stack } from "react-bootstrap";
import Image from "react-bootstrap/Image";
import { injectLeadingIconsIntoTd, removeHTMLTagsString, renderRelatedEntityText, wrapCellContentWithLeadingIcons } from "../../utils";
import {
    ColumnRender,
    getColumnAlignClass, IColumn, IIdentity
} from "./Declarations";

type Props = {
  item: any;
  column: IColumn;
  avatarUrl?: string;
  leadingIcons?: React.ReactNode;
};

const ValueRenderer: React.FC<Props> = ({ item, column, avatarUrl, leadingIcons }) => {
  let value = item[column.member];
  if (column.renderAs === ColumnRender.checkCross) {
    return (
      <td className={getColumnAlignClass(column)}>
        {wrapCellContentWithLeadingIcons(
          <FontAwesomeIcon icon={value ? faCheck : faTimes} />,
          leadingIcons
        )}
      </td>
    );
  } else if (column.renderAs === ColumnRender.synthesize) {
    return (
      <td className="text-center">
        {wrapCellContentWithLeadingIcons(<p> {removeHTMLTagsString(value)}</p>, leadingIcons)}
      </td>
    );
  } else if (column.renderAs === ColumnRender.deleted) {
    return (
      <td className={getColumnAlignClass(column)}>
        {wrapCellContentWithLeadingIcons(
          value === true ? <FontAwesomeIcon icon={faBan} className="text-danger" /> : null,
          leadingIcons
        )}
      </td>
    );
  } else if (column.renderAs === ColumnRender.customRenderer) {
    if (typeof column.customRenderer === "function") {
      return injectLeadingIconsIntoTd(column.customRenderer(item), leadingIcons);
    }
    return <td>{leadingIcons}</td>;
  } else if (column.renderAs === ColumnRender.identity) {
    const identities = Array.isArray(value)
      ? value
      : value === null
      ? []
      : [].concat(value);
    return (
      <td className="text-left">
        {wrapCellContentWithLeadingIcons(
          identities.length === 0 ? (
            "Unallocated"
          ) : (
            <Stack gap={3}>
              {identities.map((identity: IIdentity) => (
                <React.Fragment key={identity.id}>
                  <div>
                    <Image
                      roundedCircle
                      src={`${avatarUrl}/${identity.id}/${identity.avatarRevision}`}
                      className="d-inline-block me-2 image-value"
                    />
                    {identity.displayName}
                  </div>
                </React.Fragment>
              ))}
            </Stack>
          ),
          leadingIcons
        )}
      </td>
    );
  } else if (column.renderAs === ColumnRender.color) {
    return (
      <td>
        {wrapCellContentWithLeadingIcons(
          <div style={{ backgroundColor: value }}></div>,
          leadingIcons
        )}
      </td>
    );
  } else {
    const align = getColumnAlignClass(column);

    if (value === null && column.default !== undefined) {
      value = column.default;
    } else if (column.renderAs === ColumnRender.decimal) {
      if (value) value = parseFloat(value).toFixed(2);
    } else if (column.renderAs === ColumnRender.yesNo) {
      value = value ? "Yes" : "No";
    } else if (column.renderAs === ColumnRender.date) {
      if (value) value = dayjs(value).format("DD MMM YYYY");
    } else if (column.renderAs === ColumnRender.time) {
      if (value) value = dayjs(value).format("HH:mm");
    } else if (column.renderAs === ColumnRender.dateTime) {
      if (value) value = dayjs(value).format("DD MMM YYYY HH:mm");
    }
    const cellPaddingClass = align === "text-start" ? "" : "px-2";
    const cellContent = (
      <>
        {column.prefix}
        {value}
        {column.suffix}
      </>
    );
    const styledContent = column.relatedEntityLifecycle
      ? renderRelatedEntityText(cellContent, item, column.relatedEntityLifecycle)
      : cellContent;
    return (
      <td className={`${align} ${cellPaddingClass}`.trim()}>
        {wrapCellContentWithLeadingIcons(styledContent, leadingIcons)}
      </td>
    );
  }
};

export { ValueRenderer };
